import { isLiveRangeSet, matchesLiveRange } from './liveRange'
import {
  type CalendarItem,
  type CalendarItemKind,
  type CalendarFilterCategory,
  type CalendarFilterState,
} from './types'
import {
  type CalendarValueTrees,
  expandValues,
  groupOf,
  parentFilterMode,
} from './valueTree'

/**
 * The item's moment, and the content that belongs to it. Either side can be
 * missing: content whose moment is outside the window stands alone, and so does
 * a moment with nothing scheduled under it.
 */
export type CalendarFamily = Readonly<{
  parent?: CalendarItem
  children: readonly CalendarItem[]
}>

const NO_FAMILY: CalendarFamily = { children: [] }

/** The values of one list, by the kind of item that carries them. */
type PreparedValues = ReadonlyMap<CalendarItemKind, ReadonlySet<string>>

/**
 * One active category, with the chosen options already read as the values the
 * items of each kind carry.
 *
 * An option may stand for different values in different kinds — one Status
 * option covers a content workflow status and a moment's workflow summary — so
 * the translation happens here, once per filtering pass, and the matching below
 * stays the same for every category.
 */
type PreparedCategory = Readonly<{
  appliesTo: readonly CalendarItemKind[]
  exclude: PreparedValues
  getValues: (item: CalendarItem) => readonly string[] | undefined
  /**
   * Whether anything was chosen to include. Not the same question as whether
   * `include` holds anything: a category that applies to no kind on the
   * calendar translates a choice into no values at all.
   */
  hasIncludes: boolean
  /**
   * What an item has to match to be included: one value of every list. Most
   * categories have one list. A category that matches every parent has one
   * list per parent.
   */
  include: readonly PreparedValues[]
  /**
   * The values that save an item from the exclude list: the accounts ticked
   * back under a hidden channel.
   *
   * An item carries its channel as well as its account, so "hide Instagram
   * except Acme US" cannot be said by leaving values out of the exclude list —
   * the channel would hide the account all the same. It is said here instead.
   */
  spare: PreparedValues
}>

const valuesByKind = (
  category: CalendarFilterCategory,
  optionIds: readonly string[]
): Map<CalendarItemKind, Set<string>> => {
  const options = new Map(
    category.options.map((option) => [option.id, option.values])
  )

  return new Map(
    category.appliesTo.map((kind) => [
      kind,
      new Set(
        optionIds.flatMap((optionId) => {
          // An option that names no values of its own is its own value: most
          // categories filter on ids the items already carry, and an option
          // list that has not arrived yet must still match what the URL held.
          const values = options.get(optionId)
          return values === undefined ? [optionId] : (values[kind] ?? [])
        })
      ),
    ])
  )
}

/**
 * A chosen value stands for everything under it before anything else happens:
 * both lists are expanded, so excluding an account of an included channel is
 * read exactly as excluding that account on its own.
 */
const chosenValues = (
  category: CalendarFilterCategory,
  chosen: readonly string[],
  trees: CalendarValueTrees | undefined
) => valuesByKind(category, [...expandValues(chosen, trees?.[category.id])])

/**
 * The values to include, as the lists an item has to match one value of each.
 *
 * Until the tree arrives, the values are one list: the looser answer, which
 * does not hide an item only because a list is late. Once it has arrived, a
 * value it cannot place — a deleted label — is skipped, as the API skips it.
 * As a list of its own it would match nothing, and hide every item the
 * category covers. When no chosen value can be placed, the API finds nothing,
 * and so does this: one list that no item can meet.
 */
const includeLists = (
  category: CalendarFilterCategory,
  chosen: readonly string[],
  trees: CalendarValueTrees | undefined
): PreparedValues[] => {
  const tree = trees?.[category.id]
  if (chosen.length === 0) return []
  if (category.matchesEveryParent !== true || tree === undefined) {
    return [chosenValues(category, chosen, trees)]
  }

  const byParent = new Map<string, string[]>()
  chosen.forEach((valueId) => {
    if (!tree.parentOf.has(valueId) && !tree.childrenOf.has(valueId)) return

    const parentId = groupOf(valueId, tree)
    const values = byParent.get(parentId)
    if (values === undefined) byParent.set(parentId, [valueId])
    else values.push(valueId)
  })
  if (byParent.size === 0) return [new Map()]

  return [...byParent.values()].map((values) =>
    chosenValues(category, values, trees)
  )
}

const prepare = (
  state: CalendarFilterState,
  categories: readonly CalendarFilterCategory[],
  trees?: CalendarValueTrees
): PreparedCategory[] =>
  categories.flatMap((category) => {
    // A date range is not a list of values: it is matched on the item's own
    // window, and an address that wrote values under its name means nothing.
    if (category.picker !== undefined) return []

    const chosen = state.cats[category.id]
    if (chosen === undefined) return []
    if (chosen.include.length === 0 && chosen.exclude.length === 0) return []

    // A category whose parents each answer for themselves reads both lists at
    // once: the sets a person matched are ANDed together, the sets they hid are
    // subtracted, and the labels kept back from a hidden set are what save an
    // item from it. The exclude list is every hidden set's ticks and every
    // matched set's carve-outs, and both hide, so it is taken whole.
    const tree = trees?.[category.id]
    if (tree?.modeScope === 'parent') {
      const isHidden = (valueId: string) =>
        parentFilterMode(valueId, chosen, tree) === 'exclude'
      const matched = chosen.include.filter((valueId) => !isHidden(valueId))

      return [
        {
          appliesTo: category.appliesTo,
          exclude: chosenValues(category, chosen.exclude, trees),
          getValues: category.getValues,
          hasIncludes: matched.length > 0,
          include: includeLists(category, matched, trees),
          spare: chosenValues(category, chosen.include.filter(isHidden), trees),
        },
      ]
    }

    // A category filters by the list its mode names. The other list holds the
    // carve-outs under it, and what a carve-out does depends on the mode: it
    // takes a value out of what is matched, or it saves one from what is
    // hidden.
    const mode = chosen.mode ?? 'include'
    const filtered = chosen[mode]
    const carved = mode === 'include' ? chosen.exclude : chosen.include
    const none: readonly string[] = []

    return [
      {
        appliesTo: category.appliesTo,
        exclude: chosenValues(
          category,
          mode === 'exclude' ? filtered : carved,
          trees
        ),
        getValues: category.getValues,
        hasIncludes: mode === 'include' && filtered.length > 0,
        include: includeLists(
          category,
          mode === 'include' ? filtered : none,
          trees
        ),
        spare: chosenValues(
          category,
          mode === 'exclude' ? carved : none,
          trees
        ),
      },
    ]
  })

/** Whether this item's own values meet one of the values in this list. */
const meets = (
  category: PreparedCategory,
  list: PreparedValues,
  item: CalendarItem | undefined
): boolean => {
  if (item === undefined) return false

  const wanted = list.get(item.kind)
  if (wanted === undefined || wanted.size === 0) return false

  const values = category.getValues(item)
  return values !== undefined && values.some((value) => wanted.has(value))
}

/**
 * Whether this item on its own meets every include list. One member of a
 * family has to meet them all: a Paid moment and its Advocates content do not
 * add up to Paid Advocates.
 */
const isIncluded = (
  category: PreparedCategory,
  item: CalendarItem | undefined
): boolean => category.include.every((list) => meets(category, list, item))

/** Whether this category hides the item: excluded, and not ticked back. */
const hides = (
  category: PreparedCategory,
  item: CalendarItem | undefined
): boolean =>
  meets(category, category.exclude, item) &&
  !meets(category, category.spare, item)

/**
 * Whether this item survives every applied category.
 *
 * A category asks the whole family, not the item alone. An item is included
 * when it matches, or its moment matches, or any of its content matches — which
 * is what keeps the content of a matching moment on the grid, and keeps a
 * moment on the grid because of the content under it. Exclusion runs the other
 * way and only downwards: an excluded moment takes its content with it, while
 * one excluded content item leaves the rest of the family alone.
 *
 * An item that holds no value in a category no longer passes it for free. A
 * category that cannot yet say what an item holds is another matter: it has no
 * opinion, and the item passes until the answer arrives.
 *
 * Categories are met one at a time, and all of them have to be met. Within one,
 * exclusion is read after inclusion and wins — except over a value ticked back
 * under a hidden row, which is the only thing that beats it.
 */
const passes = (
  item: CalendarItem,
  family: CalendarFamily,
  categories: readonly PreparedCategory[]
): boolean =>
  categories.every((category) => {
    // Only a category that applies to this kind can say it does not know yet.
    // One that does not apply has already answered: the item holds nothing
    // here, so its family has to speak for it.
    if (
      category.appliesTo.includes(item.kind) &&
      category.getValues(item) === undefined
    ) {
      return true
    }

    if (
      category.hasIncludes &&
      !isIncluded(category, item) &&
      !isIncluded(category, family.parent) &&
      !family.children.some((child) => isIncluded(category, child))
    ) {
      return false
    }

    return !hides(category, item) && !hides(category, family.parent)
  })

/**
 * Whether the item is live inside the window a person asked for.
 *
 * The window is not a category: it is matched on the item's own dates rather
 * than on a value it carries, and it has no Include or Exclude of its own.
 */
const passesLiveRange = (
  item: CalendarItem,
  { live }: CalendarFilterState
): boolean =>
  live === undefined || !isLiveRangeSet(live) || matchesLiveRange(item, live)

/**
 * Whether one item survives the applied filters.
 *
 * Pass the family the item belongs to, or the item answers for itself alone —
 * which is right for an item with no moment in the window, and wrong for
 * anything the calendar drew a family for. `applyCalendarFilters` builds the
 * families itself, so prefer it whenever the whole window is at hand.
 *
 * Pass the value trees as well wherever a category offers a parent to choose:
 * without them a chosen channel means the channel alone, and a chosen label set
 * means nothing at all.
 */
export const matchesCalendarFilters = (
  item: CalendarItem,
  state: CalendarFilterState,
  categories: readonly CalendarFilterCategory[],
  family: CalendarFamily = NO_FAMILY,
  trees?: CalendarValueTrees
): boolean =>
  passesLiveRange(item, state) &&
  passes(item, family, prepare(state, categories, trees))

/** Each item's moment and content, for the items in this window. */
const familiesOf = (
  items: readonly CalendarItem[]
): Map<string, CalendarFamily> => {
  const byId = new Map(items.map((item) => [item.id, item]))
  const children = new Map<string, CalendarItem[]>()

  items.forEach((item) => {
    const { parentId } = item
    // A moment outside the window is no family at all: there is nothing on the
    // grid for the item to be kept by, or hidden with.
    if (parentId === undefined || !byId.has(parentId)) return

    const siblings = children.get(parentId)
    if (siblings === undefined) children.set(parentId, [item])
    else siblings.push(item)
  })

  return new Map(
    items.map((item) => [
      item.id,
      {
        children: children.get(item.id) ?? [],
        parent:
          item.parentId === undefined ? undefined : byId.get(item.parentId),
      },
    ])
  )
}

/**
 * Everything that survives the applied filters.
 *
 * The Show set is deliberately not part of this: it says which kinds are drawn,
 * not which are filtered out, so hiding the moment layer must never take its
 * content with it.
 */
export const applyCalendarFilters = (
  items: readonly CalendarItem[],
  state: CalendarFilterState,
  categories: readonly CalendarFilterCategory[],
  trees?: CalendarValueTrees
): CalendarItem[] => {
  const prepared = prepare(state, categories, trees)
  if (prepared.length === 0 && !isLiveRangeSet(state.live)) return [...items]

  const families = familiesOf(items)

  return items.filter(
    (item) =>
      passesLiveRange(item, state) &&
      passes(item, families.get(item.id) ?? NO_FAMILY, prepared)
  )
}

/**
 * The number on the filter button: every value chosen, in every category.
 *
 * The live window counts as one however many of its ends are set, because it
 * is one filter: a person set a window, not two dates.
 */
export const countCalendarFilters = (state: CalendarFilterState): number =>
  Object.values(state.cats).reduce(
    (total, category) =>
      total + category.include.length + category.exclude.length,
    isLiveRangeSet(state.live) ? 1 : 0
  )
